import { DefinitionLoader } from '@deity-io/di-data-structure-editor';
import { Box, Checkbox, Divider, FlexLayout, H4 } from '@deity-io/portal-ui';
import Authentication from 'components/Endpoints/EndpointSettings/Authentication';
import Configuration from 'components/Endpoints/EndpointSettings/Configuration';
import Connection from 'components/Endpoints/EndpointSettings/Connection';
import HTTPHeaders from 'components/Endpoints/EndpointSettings/HTTPHeaders';
import PaginationSettings from 'components/Endpoints/EndpointSettings/PaginationSettings';
import EntityPicker from 'components/FlowEditor/components/panels/FlowComponentSettings/EntityPicker';
import { EndpointActionTypes, EndpointContext } from 'contexts/EndpointContext';
import _ from 'lodash';
import type { FC } from 'react';
import { useContext } from 'react';
import { useGetModelQuery } from 'services/graphql/generated/hooks';
import { Variables } from './Properties';

type EndpointSettingProps = {
  isEditable: boolean;
  params: any;
};

export const EndpointSetting: FC<EndpointSettingProps> = ({ isEditable, params }) => {
  const ctx = useContext(EndpointContext);

  const { data: modelData } = useGetModelQuery({
    variables: {
      packageVersionUuid: ctx?.state.packageVersionUuid || '',
      uuid: (ctx?.state.responseStructure as string) || ''
    },
    skip: !(ctx?.state.packageVersionUuid && ctx?.state.uuid),
    fetchPolicy: 'cache-first'
  });

  const updateConfig = (key: string[], value: any, set: boolean = true) => {
    if (ctx) {
      if (set) {
        ctx.dispatch({ type: EndpointActionTypes.SET_VALUE, payload: { key, value } });
      } else {
        ctx.dispatch({ type: EndpointActionTypes.UNSET_VALUE, payload: { key } });
      }
    }
  };

  return (
    <>
      {!_.isEmpty(ctx?.state) ? (
        <Box>
          {ctx?.state.type && <Connection type={ctx.state.type} onChange={updateConfig} isEditable={isEditable} />}
          <Divider />
          {ctx?.state &&
            ctx.state.protocol &&
            ctx.state.method &&
            ctx.state.contentType &&
            !_.isUndefined(ctx.state.host) &&
            !_.isUndefined(ctx.state.path) && (
              <Configuration
                isEditable={isEditable}
                protocol={ctx.state.protocol}
                method={ctx.state.method}
                contentType={ctx.state.contentType}
                host={ctx.state.host}
                path={ctx.state.path}
                onChange={updateConfig}
              />
            )}
          <Divider />

          <Variables
            key="variables"
            variables={ctx?.state.variables || {}}
            onChange={updateConfig}
            isEditable={isEditable}
          />

          <Divider />
          <Authentication authentication={ctx?.state.authentication} onChange={updateConfig} isEditable={isEditable} />
          <Divider />

          <HTTPHeaders
            key="headers"
            headers={ctx?.state.headers || {}}
            onChange={updateConfig}
            isEditable={isEditable}
          />

          <Divider />
          {/* <RequestBody requestBody={ctx?.state.requestBody} onChange={updateConfig} isEditable={isEditable} /> */}
          <FlexLayout flexDirection="column" sx={{ padding: '48px' }}>
            <H4 fontWeight="semiBold">Request body structure</H4>
            <FlexLayout flexDirection="row">
              <Checkbox
                key="cb_use_resource"
                checked={ctx?.state.isResourceRequest === true}
                onChange={e => {
                  updateConfig(['isResourceRequest'], true);
                }}
                label="Expect a raw resource"
              />
              <Checkbox
                key="cb_use_schema"
                checked={ctx?.state.isResourceRequest !== true}
                onChange={e => {
                  updateConfig(['isResourceRequest'], false);
                }}
                label="Expect message data"
              />
            </FlexLayout>

            {ctx?.state.packageVersionUuid && ctx?.state.isResourceRequest !== true && (
              <EntityPicker
                description="Request body structure"
                entity="structure"
                isEditable={isEditable}
                entityUUID={ctx.state.requestStructure as string}
                onChange={(key, val) => updateConfig([key], val)}
                showEntityLink
                property="requestStructure"
              />
            )}
          </FlexLayout>
          <Divider />
          <PaginationSettings
            pagination={ctx?.state.pagination}
            // onChange={updateConfig}
            isEditable={isEditable}
            modelData={DefinitionLoader.tryLoad('responseStructure', modelData?.getModel.schema)}
          />
          <Divider />
          <FlexLayout flexDirection="column" sx={{ padding: '48px' }}>
            {/* <H4 fontWeight="semiBold">Response structure</H4> */}
            {ctx?.state.packageVersionUuid && (
              <EntityPicker
                description="Response body structure"
                entity="structure"
                isEditable={isEditable}
                entityUUID={ctx.state.responseStructure as string}
                onChange={(key, val) => {
                  updateConfig([key], val);
                }}
                showEntityLink
                property="responseStructure"
              />
            )}
          </FlexLayout>
        </Box>
      ) : (
        <H4>Loading settings...</H4>
      )}
    </>
  );
};
